import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from "react";

import {
  AVATAR_GRADIENT_URLS,
  pickAvatarGradientUrl,
} from "@/lib/avatarGradientAssets";

export { AVATAR_GRADIENT_URLS };

type AvatarGradientContextType = { getBackground: (userId: string) => string };

const AvatarGradientContext = createContext<AvatarGradientContextType | null>(
  null,
);

/**
 * Provides stable, per-user gradient backgrounds for initials avatars.
 * Assignments are kept in a ref (no re-renders) and persist for the
 * lifetime of the provider — i.e. the full app session.
 */
export function AvatarGradientProvider({ children }: { children: ReactNode }) {
  const assignmentsRef = useRef(new Map<string, number>());
  const counterRef = useRef(0);

  const getBackground = useCallback((userId: string): string => {
    if (!assignmentsRef.current.has(userId)) {
      const index = counterRef.current % AVATAR_GRADIENT_URLS.length;
      assignmentsRef.current.set(userId, index);
      counterRef.current += 1;
    }
    const index = assignmentsRef.current.get(userId)!;
    return AVATAR_GRADIENT_URLS[index]!;
  }, []);

  return (
    <AvatarGradientContext.Provider value={{ getBackground }}>
      {children}
    </AvatarGradientContext.Provider>
  );
}

/**
 * Returns a gradient asset URL for a given user's initials avatar.
 * Falls back to a hash-based pick when called outside the provider or when
 * `userId` is undefined.
 */
export function useAvatarGradient(userId: string | undefined): string {
  const ctx = useContext(AvatarGradientContext);
  if (!ctx || userId === undefined) {
    return pickAvatarGradientUrl(userId ?? "default");
  }
  return ctx.getBackground(userId);
}
